<template>
  <div>
    <p class="subtitle ml-3 mb-2">{{ $t("OrderPage[\"Payment Method\"]") }}</p>
    <el-select 
      v-model="paymentModel"
      class="order-test order-summary-selector payment mt-4" 
      :disabled="isAddOnOrder.length > 0 || editOrderNotAllowChangePayment"
      :placeholder="$t('OrderPage[\'Select\']')" 
    >
      <el-option
        v-for="item in paymentOptions"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      />
    </el-select> 
    <SwitchDialog 
      :is-show-dialog="isShowSwitchDialog"
      @close="isShowSwitchDialog=false"
      @proceed="handleChange('customerPay')"
    />   
  </div>
</template>

<script>
import SwitchDialog from "@/views/va-orderTest/order-page/order-test-panel/order-test-tabs/merch-panel/SwitchDialog/SwitchDialog.vue"
import _ from "lodash";
import { mapState, mapGetters } from "vuex";
const PAYMENT_I18N = {
  patientPayNow: 'OrderPage["Patient Pay Now - Collect Credit Card"]',
  patientPayLater: 'OrderPage["Patient Pay Later - Payment Instructions Sent to Patient"]',
  customerPay: 'OrderPage["Provider Office Bill - Card on File"]',
};

export default {
  components: {
    SwitchDialog
  },
  props: {
    paymentMethod: {
      type: String,
      default: "",
    },
    defaultSettings:{
      type: Object,
      default: () => {},
    },
    orderDraft: {
      type: Object,
      default: () => {},
    },
    customerState: {
      type: String,
      default: "",
    },
    isPatientPayLaterEligible: {
      type: Boolean,
      default: true
    },
    clientPaid: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      paymentOptions: [
        {
          value: "patientPayNow",
          label:  this.$t(PAYMENT_I18N.patientPayNow),
        },
        {
          value: "patientPayLater",
          label: this.$t(PAYMENT_I18N.patientPayLater),
        },
        {
          value: "customerPay",
          label: this.$t(PAYMENT_I18N.customerPay),
        },
      ],
      paymentValue: this.paymentMethod,
      isShowSwitchDialog: false,
    }
  },

  computed: {
    ...mapState("orderTest", ["isBackFromCheckoutPage","isAddOnOrder"]),
    ...mapState("orderTest/order", ["editOrderSampleId", "consultationFee"]),
    ...mapState("orderTest/merchandise", ["editOrderMerchList"]),
    ...mapState("orderTest/user", ["userInfo"]),

    ...mapGetters("orderTest/order", ["priceDetails"]),
    ...mapGetters("orderTest/merchandise", ["merchTotalPrice", "selectedMerchList"]),

    paymentModel: {
      get() {
        return this.paymentValue;
      },
      set(newVal) {
        if (this.beforePaymentChange(newVal)) {
          this.paymentValue = newVal;
          this.handleChange(newVal);
        } else {
          this.isShowSwitchDialog = true;
        }
      }
    },

    editOrderNotAllowChangePayment() {
      // 03/12/2025: not allow change payment method for edit order if original order payment method is wellProz
      // 03/20/2025: not allow change payment method for edit order if original order payment method is netPay
      if (["wellProz", "netPay"].includes(this.paymentMethod)) {
        return true;
      }
      // edit order not allow change from patientPayNow/customerPay to other payment methods 
      // if the original order was paid partially or fully
      // 03/07/2025: not allow change from customerPay to other payment methods, but allow change from patientPayNow to patientPayLater
      if (this.editOrderSampleId) {
        let clientPaid = this.clientPaid ? this.clientPaid : 0;
        return this.orderDraft && this.orderDraft.chargeMethod === "customerPay" && clientPaid > 0;
      }
      return false;
    },
  },

  watch: {
    defaultSettings() {
      this.getDefaultPaymentMethod();
      this.handleExceptionCustomerState();
    },
    '$i18n.locale'() {
      this.rebuildPaymentOptionLabels();
      this.$forceUpdate();
    },
    orderDraft() {
      this.handleExceptionCustomerState();
      if (this.editOrderSampleId) { 
        this.removeEditOrderCustomerPayOption();
      }
    },
    paymentMethod(newVal) {
      this.paymentValue = newVal;
      if (newVal === "patientPayLater") {
        this.$store.dispatch("orderTest/order/setPlannedOrderList", []);
      }
      // try call function here to handle ppl options
      // in draft/edit order/addon order
      this.handlePatientPayLaterOption(this.isPatientPayLaterEligible);
    },
    customerState(newVal) {
      if (["NY", "NJ", "RI"].includes(newVal)) {
        this.paymentOptions.pop();
      }
    },
    isPatientPayLaterEligible(newVal) {
      this.handlePatientPayLaterOption(newVal);
    },
    editOrderSampleId(newVal) {
      if (newVal) {
        this.removeEditOrderCustomerPayOption();
      }
    }
  },

  mounted() {    
    if (!this.isBackFromCheckoutPage) {
      this.getDefaultPaymentMethod();
    } 
    this.handleExceptionCustomerState();
    if (["NY", "NJ", "RI"].includes(this.customerState)) {
      this.paymentOptions.pop();
    }
    this.handlePatientPayLaterOption(this.isPatientPayLaterEligible);
    if (this.editOrderSampleId) {
      this.removeEditOrderCustomerPayOption();
    }
  },

  methods: {
    getDefaultPaymentMethod() {
      if (this.orderDraft && !_.isEmpty(this.orderDraft)) {
        return;
      }
      if (this.defaultSettings.chargeMethod) {
        this.$store.dispatch("orderTest/order/setPaymentMethod", this.defaultSettings.chargeMethod);
      }
    },

    beforePaymentChange(value) {
      // if want to change to provider pay and
      // have markup/consultation/merch
      // don't change, open dialog
      if (value === "customerPay" // change to provider pay
        && ( 
          // have consultation fee
          (this.priceDetails.consultationFee > 0 
            && (this.priceDetails.markupPrice > 0 || this.priceDetails.orderTestsPrice > 0)
          )
         ||  // OR  have markup
          this.priceDetails.markupPrice > 0
         || // OR have merchandise
          this.merchTotalPrice > 0
        ) 
      ) {
        return false;
      }
      return true;
    },
    handleChange(value) {
      this.$store.dispatch("orderTest/order/setPaymentMethod", value);
      if (value === "patientPayLater") {
        this.$store.dispatch("orderTest/order/setPlannedOrderList", []);
      }
      if (this.isShowSwitchDialog) {
        this.isShowSwitchDialog = false;
      }
    },
    handleExceptionCustomerState() {
      if (["NY", "NJ", "RI"].includes(this.customerState) && this.$store.state.orderTest.order.paymentMethod !== "patientPayLater") {
        this.$store.dispatch("orderTest/order/setPaymentMethod", "patientPayNow");
      }
    },
    handlePatientPayLaterOption(isEligible) {
      let isFound = this.paymentOptions.find(option => option.value === "patientPayLater");
      if (!isEligible) {
        if (isFound) {
          this.paymentOptions.splice(1, 1);
        }
        // replace patientPayLater with other option
        if (this.paymentValue === "patientPayLater") {
          this.paymentValue = "patientPayNow";
          this.$store.dispatch("orderTest/order/setPaymentMethod", this.paymentValue);
        }
      } else {
        // add patient pay later option if no this option
        if (!isFound) {
          let patientPayLaterOption = {
            value: "patientPayLater",
            label: this.$t("OrderPage[\"Patient Pay Later - Payment Instructions Sent to Patient\"]"),
          };
          this.paymentOptions.splice(1, 0, patientPayLaterOption);
        }
      }
    },
    removeEditOrderCustomerPayOption() {
      // remove customer pay option if patient pay now for edit order
     if (this.orderDraft && this.orderDraft.chargeMethod === "patientPayNow") {
        let customerPayOption = this.paymentOptions.find(option => option.value === "customerPay");
        if (customerPayOption) {
          this.paymentOptions.splice(this.paymentOptions.indexOf(customerPayOption), 1);
        }
      }
    },
    rebuildPaymentOptionLabels() {
      this.paymentOptions = this.paymentOptions.map(opt => ({
        ...opt,
        label: this.$t(PAYMENT_I18N[opt.value]),
      }));
    },
  },
}
</script>


<style scoped>
.subtitle {
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  letter-spacing: 0.4576px;
}
</style>